import type { Language } from './types';
import { LANG_DEFAULT } from './types';

/**
 * Tiny i18n singleton.
 *
 *  - English only: ORS directions have no Hungarian locale, so HU UI
 *    would mix with English step text.
 *  - Translation lookup is a flat Record<key, { hu: string; en: string }>.
 *  - `t(key)` is synchronous and reads the current language at call time.
 *  - `setLang(lang)` updates the active language, persists it to
 *    localStorage, then dispatches a `navi:langchange` CustomEvent on
 *    window so the UI layer (mountUi) can re-apply static labels.
 *  - formatDistance / formatSpeed are language-neutral. formatDuration
 *    swaps unit suffixes ("ó / perc" vs "h / min") so it's the only
 *    formatter that actually consults currentLang.
 *
 * Design notes:
 *  - We DO use a single `let currentLang` rather than a context because
 *    every UI consumer in this app is a thin DOM-binding layer that
 *    already re-renders on each event (setHud, setStatus, etc.). Reactive
 *    frameworks are overkill.
 *  - No bundler-side message extraction — messages are a literal map
 *    that ships in the JS bundle. The footprint is <2 KB minified and
 *    there's no runtime risk of missing translations.
 */

const STORAGE_KEY = 'navi.lang';

type Messages = Record<string, { hu: string; en: string }>;

/**
 * All user-visible strings the app emits. Keys are dot-namespaced by
 * location so a missing string is obvious in the rendered DOM during
 * development (`t('chip.radar.warning') → "chip.radar.warning"`).
 *
 * Anything NOT here is considered technical data (coordinates, OSM
 * tags, distractor unit symbols like "m" / "km") and stays in code.
 */
const messages: Messages = {
  /* ---------- Search / hits ---------- */
  'search.placeholder': {
    hu: 'Cím vagy hely keresése…',
    en: 'Search address or place…',
  },
  'search.clear': {
    hu: 'Keresés törlése',
    en: 'Clear search',
  },

  /* ---------- Status messages (transient text below steps) ---------- */
  'status.searchPrompt': {
    hu: 'Kereséshez írj be egy címet vagy helyet.',
    en: 'Type an address or place to search.',
  },
  'status.noResult': {
    hu: 'Nincs találat a keresésre.',
    en: 'No search results.',
  },
  'status.placeRefine': {
    hu: 'Írd az utca nevét a vessző után, vagy küldd el a települést célnak.',
    en: 'Type a street after the comma, or submit to use this place as destination.',
  },
  'status.routePlanning': {
    hu: 'Útvonal tervezése…',
    en: 'Planning route…',
  },
  'status.routeReady': {
    hu: 'Útvonal kész.',
    en: 'Route ready.',
  },
  'status.rerouting': {
    hu: 'Újratervezés…',
    en: 'Rerouting…',
  },
  'status.reroutingDone': {
    hu: 'Újratervezés kész.',
    en: 'Reroute complete.',
  },
  'status.destination': {
    hu: 'Cél: ',
    en: 'Destination: ',
  },
  'status.destinationUpdated': {
    hu: 'Cél frissítve: ',
    en: 'Destination updated: ',
  },
  'status.multiStopReady': {
    hu: 'Útvonal kész (több állomás).',
    en: 'Multi-stop route ready.',
  },
  'status.addStopArmed': {
    hu: 'Válaszd ki a következő állomást (keresés vagy hosszú bökés a térképen).',
    en: 'Pick the next stop (search or long-press the map).',
  },
  'status.addStopNeedDest': {
    hu: 'Előbb adj meg egy célt, aztán állomást.',
    en: 'Set a destination first, then add a stop.',
  },
  'status.stopsMax': {
    hu: 'Maximum 3 állomás.',
    en: 'Maximum 3 stops.',
  },
  'search.addStopPlaceholder': {
    hu: 'Következő állomás keresése…',
    en: 'Search next stop…',
  },
  'status.mapPickPlanning': {
    hu: 'Útvonal a térképen kijelölt ponthoz…',
    en: 'Planning route to map pin…',
  },
  'status.navActive': {
    hu: 'Navigáció aktív — GPS követés indul.',
    en: 'Navigation active — GPS tracking started.',
  },
  'status.navPaused': {
    hu: 'Navigáció szüneteltetve — az útvonal előnézet megmaradt.',
    en: 'Navigation paused — route preview preserved.',
  },
  'status.navResumed': {
    hu: 'Navigáció folytatása — GPS követés újraindul.',
    en: 'Navigation resumed — GPS tracking restarted.',
  },
  'status.navStopped': {
    hu: 'Navigáció leállítva — az útvonal előnézet megmaradt.',
    en: 'Navigation stopped — route preview preserved.',
  },
  'status.arrived': {
    hu: 'Megérkeztél a célhoz!',
    en: 'You have arrived at your destination!',
  },
  'status.arrivedAt': {
    hu: 'Megérkeztél: ',
    en: 'You have arrived: ',
  },
  'status.gpsUnsupported': {
    hu: 'A böngésző nem támogatja a geolokációt.',
    en: 'Your browser does not support geolocation.',
  },
  'status.gpsDenied': {
    hu: 'Helymeghatározás megtagadva — engedélyezd a böngésző beállításokban.',
    en: 'Geolocation denied — enable it in browser settings.',
  },
  'status.gpsDeniedHelp': {
    hu: 'A helymeghatározás le van tiltva ezen a webhelyen. A böngészőben koppints az URL melletti ikonra (lakat vagy információ ikon) → „Helyhozzáférés" → „Engedélyezés", majd frissítsd az oldalt.',
    en: 'Geolocation is blocked for this site. In the browser, tap the icon next to the address bar (lock or info icon) → "Location" → "Allow", then reload the page.',
  },
  'status.gpsUnavailable': {
    hu: 'A készülék helymeghatározása átmenetileg nem elérhető.',
    en: 'Device geolocation is temporarily unavailable.',
  },
  'status.gpsTimeout': {
    hu: 'A helymeghatározás túl sokáig tartott.',
    en: 'Geolocation timed out.',
  },
  'status.gpsError': {
    hu: 'Helymeghatározási hiba: ',
    en: 'Geolocation error: ',
  },
  'status.gpsNavStopped': {
    hu: 'GPS hiba — a navigáció leállt, az útvonal előnézet megmaradt.',
    en: 'GPS error — navigation stopped, route preview preserved.',
  },

  /* ---------- HUD chip labels ---------- */
  'chip.radar.label': {
    hu: '⚠️ Sebességmérő',
    en: '⚠️ Speed camera',
  },
  'chip.roadwork.construction': {
    hu: '⚠️ Útépítés',
    en: '⚠️ Roadworks',
  },
  'chip.roadwork.closure': {
    hu: '⛔ Lezárt út',
    en: '⛔ Road closed',
  },

  /* ---------- HUD static labels (per-language suffix) ---------- */
  'hud.speed': { hu: 'Km/h', en: 'Km/h' },
  'hud.distance': { hu: 'Hátralévő', en: 'Remaining' },
  'hud.time': { hu: 'Idő', en: 'Time' },
  'hud.eta': { hu: 'Érkezés', en: 'ETA' },

  /* ---------- HUD action buttons ---------- */
  'hud.action.pause': { hu: 'Szünet', en: 'Pause' },
  'hud.action.resume': { hu: 'Folytatás', en: 'Resume' },
  'hud.action.expand': { hu: 'Részletek', en: 'Details' },
  'hud.action.stop': { hu: 'Leállítás', en: 'Stop' },
  'hud.action.tools': { hu: 'Eszközök', en: 'Tools' },
  'hud.action.toolsHint': {
    hu: 'Navigációs eszközök megjelenítése',
    en: 'Show navigation tools',
  },
  'hud.action.toolsHide': {
    hu: 'Navigációs eszközök elrejtése',
    en: 'Hide navigation tools',
  },

  /* ---------- HUD view-mode + auto zoom ---------- */
  'hud.view.north': { hu: 'Észak', en: 'North' },
  'hud.view.2d': { hu: '2D', en: '2D' },
  'hud.view.tilted': { hu: 'Döntött', en: 'Tilted' },
  'hud.view.toggle': {
    hu: 'Nézet váltása (észak / 2D / döntött)',
    en: 'Switch map view (North / 2D / Tilted)',
  },
  'map.compass': {
    hu: 'Északi nézet',
    en: 'North view',
  },
  'map.compassHint': {
    hu: 'Váltás északi nézetre',
    en: 'Switch to north-up view',
  },
  'weather.chip': {
    hu: 'Időjárás a jelenlegi helyen',
    en: 'Weather at your current location',
  },
  'weather.chipTitle': {
    hu: 'Időjárás · Open-Meteo',
    en: 'Weather · Open-Meteo',
  },
  'hud.action.autozoom': { hu: 'Auto zoom', en: 'Auto zoom' },
  'hud.action.autozoomHint': {
    hu: 'Vissza az automatikus nagyításhoz (követés + következő kanyar)',
    en: 'Restore automatic zoom (follow + next turn framing)',
  },
  'hud.action.overview': { hu: 'Teljes út', en: 'Overview' },
  'hud.action.overviewHint': {
    hu: 'Teljes útvonal a térképen',
    en: 'Show the whole route on the map',
  },
  'hud.action.routeOpts': { hu: 'Útvonal', en: 'Route' },
  'hud.action.routeOptsHint': {
    hu: 'Útvonal preferencia és elkerülések',
    en: 'Route preference and avoidances',
  },
  'route.opts.title': { hu: 'Útvonal beállítások', en: 'Route options' },
  'route.opts.preference': { hu: 'Preferencia', en: 'Preference' },
  'route.opts.recommended': { hu: 'Ajánlott', en: 'Recommended' },
  'route.opts.fastest': { hu: 'Gyors', en: 'Fastest' },
  'route.opts.shortest': { hu: 'Rövid', en: 'Shortest' },
  'route.opts.avoid': { hu: 'Kerüld', en: 'Avoid' },
  'route.opts.highways': { hu: 'Autópálya', en: 'Highways' },
  'route.opts.ferries': { hu: 'Komp', en: 'Ferries' },
  'route.opts.tollways': { hu: 'Útdíj', en: 'Toll roads' },

  /* ---------- Info panel static labels ---------- */
  'info.distance': { hu: 'Távolság', en: 'Distance' },
  'info.time': { hu: 'Idő', en: 'Time' },
  'info.clear': { hu: 'Útvonal törlése', en: 'Clear route' },
  'info.collapse': { hu: '⌄ Részletek elrejtése', en: '⌄ Hide details' },
  'info.expand': { hu: '⌃ Részletek mutatása', en: '⌃ Show details' },
  'info.share': { hu: '↗ Megosztás', en: '↗ Share' },
  'info.shareHint': {
    hu: 'Megosztás — üzenet, email, közösségi…',
    en: 'Share — Messages, email, social…',
  },
  'info.addStop': { hu: '+ Állomás hozzáadása', en: '+ Add stop' },
  'info.addStopHint': {
    hu: 'Új állomás az útvonalhoz (max. 3)',
    en: 'Add another stop to the route (max 3)',
  },
  'info.addStopArmed': { hu: 'Válassz állomást…', en: 'Pick a stop…' },
  'info.stops': { hu: 'Állomások', en: 'Stops' },
  'info.stopVia': { hu: 'Állomás', en: 'Stop' },
  'info.stopFinal': { hu: 'Cél', en: 'Destination' },
  'info.removeStop': { hu: 'Állomás törlése', en: 'Remove stop' },
  'info.shareDone': {
    hu: 'Link a vágólapra másolva (ezen az eszközön nincs natív megosztás).',
    en: 'Link copied (no native share sheet on this device).',
  },
  'info.shareFail': {
    hu: 'A megosztás nem sikerült.',
    en: 'Sharing failed.',
  },

  'fav.home': { hu: 'Haza', en: 'Home' },
  'fav.work': { hu: 'Munka', en: 'Work' },
  'fav.last': { hu: 'Utolsó útvonal', en: 'Last route' },
  'fav.menu': { hu: 'Kedvencek', en: 'Favorites' },
  'fav.set': { hu: 'Beállít', en: 'Set' },
  'fav.saveHome': {
    hu: 'Haza mentése a jelenlegi helyről',
    en: 'Save home from current location',
  },
  'fav.saveWork': {
    hu: 'Munka mentése a jelenlegi helyről',
    en: 'Save work from current location',
  },
  'fav.updateHome': {
    hu: 'Haza frissítése a jelenlegi helyre',
    en: 'Update home to current location',
  },
  'fav.updateWork': {
    hu: 'Munka frissítése a jelenlegi helyre',
    en: 'Update work to current location',
  },
  'fav.saveHereHint': {
    hu: 'Mentés: ⬇ a jelenlegi GPS helyről',
    en: 'Save with ⬇ from current GPS location',
  },
  'fav.here': { hu: 'Jelenlegi hely', en: 'Current location' },
  'fav.star': { hu: 'Kedvenc', en: 'Favorite' },
  'fav.saveAs': { hu: 'Mentés ide…', en: 'Save as…' },
  'fav.namePrompt': {
    hu: 'Kedvenc neve (a cím megmarad):',
    en: 'Favorite name (address is kept):',
  },
  'fav.rename': { hu: 'Átnevezés', en: 'Rename' },
  'fav.renamed': { hu: 'Kedvenc átnevezve.', en: 'Favorite renamed.' },
  'fav.resolvingAddress': {
    hu: 'Cím meghatározása…',
    en: 'Looking up address…',
  },
  'fav.savedList': { hu: 'Kedvencek', en: 'Saved' },
  'fav.recentList': { hu: 'Legutóbbiak', en: 'Recent' },
  'fav.savedEmpty': {
    hu: 'Még nincs mentett hely. A találatoknál a ★ gombbal add hozzá.',
    en: 'No saved places yet. Use ★ on a search result to add one.',
  },
  'fav.recentEmpty': {
    hu: 'Még nincs legutóbbi cél.',
    en: 'No recent destinations yet.',
  },
  'fav.emptyHint': {
    hu: 'Nincs beállítva',
    en: 'Not set',
  },
  'fav.setFromDestination': {
    hu: 'Beállítás a jelenlegi célból',
    en: 'Set from current destination',
  },
  'fav.needLocation': {
    hu: 'Nincs GPS pozíció. Engedélyezd a helymeghatározást, vagy válassz célt.',
    en: 'No GPS fix. Enable location, or pick a destination first.',
  },
  'fav.clearHome': { hu: 'Haza törlése', en: 'Clear home' },
  'fav.clearWork': { hu: 'Munka törlése', en: 'Clear work' },
  'fav.remove': { hu: 'Eltávolítás', en: 'Remove' },
  'fav.homeSaved': {
    hu: 'Haza mentve.',
    en: 'Home saved.',
  },
  'fav.workSaved': {
    hu: 'Munka mentve.',
    en: 'Work saved.',
  },
  'fav.saved': {
    hu: 'Kedvenc mentve.',
    en: 'Favorite saved.',
  },
  'fav.needDestination': {
    hu: 'Nincs aktív cél. Válassz találatot ★-ral, vagy tervezz útvonalat, majd nyomd a Beállítot.',
    en: 'No active destination. Save from a search ★, or plan a route then tap Set.',
  },
  'fav.restored': {
    hu: 'Utolsó útvonal visszaállítva.',
    en: 'Last route restored.',
  },
  'fav.noLast': {
    hu: 'Nincs mentett útvonal.',
    en: 'No saved route.',
  },

  'hud.then': { hu: 'Majd', en: 'Then' },
  'hud.lane.keepLeft': { hu: 'Maradj balra', en: 'Keep left' },
  'hud.lane.keepRight': { hu: 'Maradj jobbra', en: 'Keep right' },
  'hud.lane.exit': { hu: '{n}. kijárat', en: 'Exit {n}' },

  'power.saver': { hu: 'Takarékos mód', en: 'Power saver' },
  'power.saverOn': {
    hu: 'Takarékos be — ritkább GPS, kevesebb anim, idle halványítás',
    en: 'Power saver on — slower GPS, less motion, idle dim',
  },
  'power.saverOff': {
    hu: 'Takarékos ki',
    en: 'Power saver off',
  },

  'status.deeplink': {
    hu: 'Megosztott cél betöltése…',
    en: 'Opening shared destination…',
  },
  'status.offlineRoute': {
    hu: 'Offline — mentett útvonal elérhető.',
    en: 'Offline — saved route available.',
  },

  /* ---------- Nav toggle button ---------- */
  'nav.start': { hu: '▶ Indítás', en: '▶ Start' },
  'nav.stop': { hu: '■ Leállítás', en: '■ Stop' },
  'nav.resume': { hu: '▶ Folytatás', en: '▶ Resume' },

  /* ---------- Topbar / chrome ---------- */
  'topbar.recenter': { hu: 'Auto zoom', en: 'Auto zoom' },
  'topbar.lang': { hu: 'Nyelvválasztás', en: 'Language' },
  'topbar.langShort.hu': { hu: 'HU', en: 'HU' },
  'topbar.langShort.en': { hu: 'EN', en: 'EN' },

  /* ---------- Time / distance unit words used by formatters ---------- */
  'fmt.minuteShort': { hu: 'perc', en: 'min' },
  'fmt.hourShort': { hu: 'ó', en: 'h' },
  'fmt.minuteTiny': { hu: 'p', en: 'min' },
};

/* ---------------------------------------------------------
   Runtime state
   --------------------------------------------------------- */

/**
 * Read persisted language from localStorage at module-eval time so that
 * the very first `t()` call (which happens during DOM binding) yields
 * the user's saved preference, not a momentary HU flash.
 */
function loadInitialLang(): Language {
  if (typeof window === 'undefined') return LANG_DEFAULT;
  try {
    window.localStorage.setItem(STORAGE_KEY, 'en');
  } catch {
    /* access-blocked iframe / private mode */
  }
  return 'en';
}

let currentLang: Language = loadInitialLang();

/** Subscriber registry. Each callback receives the new language. */
const listeners = new Set<(lang: Language) => void>();

/**
 * Look up a translation key in the active language. Missing keys are
 * returned verbatim — visible during development, harmless in prod
 * because every key we use ships here in this file.
 */
export function t(key: string): string {
  const m = messages[key];
  if (!m) return key;
  return m[currentLang] ?? m[LANG_DEFAULT] ?? key;
}

/** Current language read-only accessor. */
export function getLang(): Language {
  return currentLang;
}

/**
 * Set the active language. Persistence + listener dispatch happen
 * synchronously inside the same call, so callback order matches
 * subscription order. Listeners must not throw — i18n.ts+ui.ts are
 * the only ones that subscribe.
 */
export function setLang(lang: Language): void {
  if (lang !== 'en') return;
  if (lang === currentLang) return;
  currentLang = lang;
  try {
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(STORAGE_KEY, lang);
    }
  } catch {
    /* swallowed */
  }
  for (const cb of listeners) {
    try {
      cb(lang);
    } catch {
      /* a buggy subscriber must not break the rest of the chain */
    }
  }
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('navi:langchange', { detail: lang }));
  }
}

/** Subscribe to language change events. Returns an unsubscribe fn. */
export function onLangChange(cb: (lang: Language) => void): () => void {
  listeners.add(cb);
  return () => {
    listeners.delete(cb);
  };
}

/* ---------------------------------------------------------
   Language-aware formatters
   ---------------------------------------------------------
   Distance and Speed are language-neutral ("1.2 km", "54 km/h"); only
   formatDuration swaps unit suffixes ("ó / p" vs "h / min"). Keeping
   them simple — no ICU MessageFormat, no plural rules. */

export function formatDistance(meters: number): string {
  if (!Number.isFinite(meters) || meters <= 0) return '—';
  if (meters < 1000) return `${Math.round(meters)} m`;
  return `${(meters / 1000).toFixed(meters < 10000 ? 1 : 0)} km`;
}

/**
 * Format a duration measurement.
 *
 *   < 60 min  → "12 perc" / "12 min"
 *   1+ h      → "2 ó" / "2 h"
 *   1 h 30 m  → "1 ó 30 p" / "1 h 30 min"
 *
 * The tiny "p" / "min" suffix in the hour-row is localizable via
 * `fmt.minuteTiny` so the layout doesn't blow out wide on phones.
 */
export function formatDuration(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds <= 0) return '—';
  const m = Math.round(seconds / 60);
  if (m < 60) return `${m} ${t('fmt.minuteShort')}`;
  const h = Math.floor(m / 60);
  const r = m % 60;
  if (r === 0) return `${h} ${t('fmt.hourShort')}`;
  return `${h} ${t('fmt.hourShort')} ${r} ${t('fmt.minuteTiny')}`;
}

/** Pure m/s → km/h. Language-neutral on purpose. */
export function formatSpeed(mps: number | null): string {
  if (mps === null || !Number.isFinite(mps) || mps < 0) return '—';
  return `${Math.round(mps * 3.6)}`;
}

/** 24h "HH:MM". Language-neutral. */
export function formatEtaClock(date: Date | null): string {
  if (!date) return '—';
  const hh = date.getHours().toString().padStart(2, '0');
  const mm = date.getMinutes().toString().padStart(2, '0');
  return `${hh}:${mm}`;
}
